import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

const PhotoUploader = ({ onUpload, uploading, albumTitle }) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );

    if (files.length > 0) {
      onUpload(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onUpload(files);
    }
    // Reset input value to allow same file selection
    e.target.value = '';
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 sm:p-8">
      <div
        className={`border-2 border-dashed rounded-lg p-8 sm:p-12 text-center transition-all cursor-pointer ${
          dragActive
            ? 'border-black bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!uploading ? openFileDialog : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />

        {uploading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 font-light text-sm sm:text-base">Uploading photos...</p>
          </motion.div>
        ) : (
          <>
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-black rounded-sm"></div>
            </div>
            <h3 className="text-lg sm:text-xl font-light text-black mb-3">
              {dragActive ? 'Drop photos here' : 'Upload Photos'}
            </h3>
            <p className="text-gray-600 mb-4 sm:mb-6 font-light text-sm sm:text-base px-4">
              Drag and drop your files here, or click to browse
            </p>
            <button
              type="button"
              className="bg-black text-white px-6 sm:px-8 py-2 sm:py-3 rounded-md hover:bg-gray-800 font-medium transition-all btn-primary text-sm sm:text-base"
            >
              Choose Files
            </button>
          </>
        )}
      </div>
      <div className="mt-4 text-xs sm:text-sm text-gray-500 text-center font-light">
        <p>Supported: JPG, PNG, GIF, MP4, MOV • Max 500MB per file</p>
      </div>
    </div>
  );
};

export default PhotoUploader;